/**
 * CircleInviteSheet — PPL-215 (web: circle invite UI).
 *
 * The "invite people" sheet a circle ADMIN opens from CircleDetail /
 * CircleMembers. It mirrors the rooms composer's PERSON step (RoomList.tsx
 * ComposeView) rather than reinventing one:
 *
 *   pick a contact  →  resolveTetherHandle, then lookupUserByEmail
 *   type an @handle →  resolveTetherHandle
 *   type name+email →  lookupUserByEmail
 *
 * …and then calls `createCircleInvite` with whichever the detection produced:
 *   on pplkpr      → { inviteeId, channel: 'in_app' }   (rail + push + email)
 *   off pplkpr     → { inviteeEmail, inviteeName, channel: 'email' }
 *   "I'll send it" → { channel: 'link' }  → copy the returned inviteUrl
 *
 * Identity register — circles are the PERSONAL half, so everything rendered
 * here is a FIRST NAME (the handle appears only as a fallback / as the thing
 * the admin typed to find someone). This deliberately does NOT follow the
 * anonymous handle-only rule that governs DMs, PPLTalk and discovery.
 *
 * Permission is enforced server-side (`assertActiveAdmin` runs BEFORE any
 * invitee lookup so a non-admin can't use the callable as an email oracle);
 * the admin-only rendering here is UI affordance, not the gate.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AtSign, Check, Copy, Link2, Loader, Mail, Search, Users, X } from 'react-feather';
import MonoAvatar from './MonoAvatar';
import { getContacts, getContactChannels } from '../services/contactService';
import { createCircleInvite } from '../services/circleService';
import { lookupUserByEmail, resolveTetherHandle } from '../services/roomService';
import type { Contact } from '../types/contacts';
import type { CircleInviteChannel, CreateCircleInviteResponse } from '../types/circles';

// ─── helpers ─────────────────────────────────────────────────────────────────

const EMAIL_RE = /^\S+@\S+\.\S+$/;

/**
 * Display stand-ins used when nothing better is known about the invitee. They
 * read naturally in the sheet's copy but are NOT names: the server persists
 * `inviteeName` and greets the placeholder invite email with it, so none of
 * these may ever be sent.
 */
const NAME_PLACEHOLDERS = new Set(['they', 'them', 'this person']);

/**
 * Personal register: the lookup callables return a full `displayName`
 * ("Ada Lovelace"), falling back to the handle — or, for the email lookup, to
 * the queried address. Take the first name; never surface a raw email as
 * someone's name.
 */
function personalFirstName(displayName: string | undefined, tetherHandle?: string | null): string {
  const raw = (displayName || '').trim();
  const handle = (tetherHandle || '').replace(/^@/, '');
  // Both callables fall back to the value they were QUERIED with when the user
  // has no firstName: the email address (lookupUserByEmail) or the bare handle
  // (resolveTetherHandle). Neither is a name — a bare handle rendered as a
  // first name reads like one, so surface it as an explicit @handle instead.
  const isHandleEcho = !!handle && raw.toLowerCase() === handle.toLowerCase();
  if (raw && !raw.includes('@') && !isHandleEcho) return raw.split(/\s+/)[0];
  if (handle) return `@${handle}`;
  // Deliberately EMPTY, not a generic stand-in: the server result degrading to
  // nothing usable is exactly when the caller's own local knowledge (the
  // contact's name, or the name the admin just typed) is the better label. A
  // truthy stand-in here would make every `|| contactFirstName` fallback dead
  // code and show "this person" for someone the admin has a real name for.
  // Each call site supplies the final generic fallback.
  return '';
}

/** A resolved invite target, whichever tab produced it. */
interface InviteTarget {
  /** firstName for a personal-register label. */
  firstName: string;
  /** uid when the person is on pplkpr. */
  uid?: string;
  /** Delivery address when they are not (or when we found one on file). */
  email?: string;
  tetherHandle?: string | null;
}

type Tab = 'people' | 'handle' | 'email';

type Detection =
  | { status: 'idle' }
  | { status: 'detecting' }
  | { status: 'on-pplkpr'; target: InviteTarget }
  | { status: 'not-on-pplkpr'; target: InviteTarget };

// ─── shared inline styles ────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 12,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  outline: 'none',
  color: '#fff',
  fontSize: 13.5,
  fontFamily: 'inherit',
};

function TabButton({ on, onClick, icon, label }: {
  on: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
        color: on ? '#f0abfc' : 'rgba(232,220,250,0.65)',
        background: on ? 'rgba(217,70,239,0.14)' : 'rgba(255,255,255,0.04)',
        border: on ? '1px solid rgba(217,70,239,0.4)' : '1px solid rgba(255,255,255,0.09)',
        whiteSpace: 'nowrap',
      }}
    >
      {icon} {label}
    </button>
  );
}

function ChannelRow({ on, onClick, label, sub, icon }: {
  on: boolean;
  onClick: () => void;
  label: string;
  sub: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        appearance: 'none', cursor: 'pointer', width: '100%', color: '#fff',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 13px', borderRadius: 12, textAlign: 'left', fontFamily: 'inherit',
        background: on ? 'rgba(217,70,239,0.10)' : 'rgba(255,255,255,0.03)',
        border: on ? '1px solid rgba(217,70,239,0.4)' : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <span style={{
        width: 30, height: 30, borderRadius: 9, flexShrink: 0,
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'rgba(232,220,250,0.55)', marginTop: 2 }}>{sub}</span>
      </span>
      <span style={{
        width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
        border: on ? '4px solid #d946ef' : '1.5px solid rgba(255,255,255,0.25)',
        background: on ? '#fff' : 'transparent',
      }} />
    </button>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

export interface CircleInviteSheetProps {
  circleId: string;
  circleName: string;
  /** Signed-in admin's uid — used to read their own contacts subcollections. */
  uid: string | null;
  /**
   * Closing is also the refresh point — callers reload the circle here rather
   * than mid-flow, so a reload can never unmount the sheet under the user.
   */
  onClose: () => void;
}

export default function CircleInviteSheet({
  circleId, circleName, uid, onClose,
}: CircleInviteSheetProps) {
  const [tab, setTab] = useState<Tab>('people');

  // People tab
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Contact | null>(null);

  // Handle tab
  const [handleInput, setHandleInput] = useState('');
  // Email tab
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  // People tab fallback when a contact has no address on file
  const [typedEmail, setTypedEmail] = useState('');

  const [detection, setDetection] = useState<Detection>({ status: 'idle' });
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [offChannel, setOffChannel] = useState<'email' | 'link'>('email');

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateCircleInviteResponse | null>(null);
  /** Which channel produced `result` — the success view describes that, not the current picker. */
  const [sentChannel, setSentChannel] = useState<CircleInviteChannel>('in_app');
  const [copied, setCopied] = useState(false);

  // ── contacts ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    setContactsLoading(true);
    void (async () => {
      try {
        const { contacts: rows } = await getContacts(uid, 200);
        if (!cancelled) setContacts(rows);
      } catch (err) {
        console.warn('circle invite: getContacts failed', err);
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // Escape closes, same as the backdrop — a modal that only closes by mouse is
  // a trap for keyboard users.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filteredContacts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => (
      `${c.firstName || ''} ${c.lastName || ''} ${c.nickname || ''}`.toLowerCase().includes(q)
    ));
  }, [contacts, query]);

  // ── detection for a picked contact (mirrors the rooms composer) ───────────
  useEffect(() => {
    if (!picked) return;
    let cancelled = false;
    setDetection({ status: 'detecting' });
    setLookupError(null);
    setTypedEmail('');
    const contactFirstName = (picked.firstName || picked.nickname || '').trim() || 'them';
    void (async () => {
      let email = (picked.email || '').trim();
      try {
        // Handle first — the fast path, and it needs no email.
        if (picked.tetherHandle) {
          const r = await resolveTetherHandle(picked.tetherHandle);
          if (cancelled) return;
          if (r.found) {
            setDetection({
              status: 'on-pplkpr',
              target: {
                firstName: personalFirstName(r.displayName, r.tetherHandle) || contactFirstName || 'this person',
                uid: r.uid,
                tetherHandle: r.tetherHandle ?? null,
              },
            });
            return;
          }
        }
        // The person doc rarely carries an email — the contacts subcollection does.
        if (!email && uid) {
          try {
            const ch = await getContactChannels(uid, picked.id);
            if (cancelled) return;
            if (ch.email) email = ch.email;
          } catch (err) {
            console.warn('circle invite: contacts subcollection read failed', err);
          }
        }
        if (email) {
          const r = await lookupUserByEmail(email);
          if (cancelled) return;
          if (r.found) {
            setDetection({
              status: 'on-pplkpr',
              target: {
                firstName: personalFirstName(r.displayName, r.tetherHandle) || contactFirstName || 'this person',
                uid: r.uid,
                tetherHandle: r.tetherHandle ?? null,
              },
            });
            return;
          }
        }
        if (cancelled) return;
        setDetection({
          status: 'not-on-pplkpr',
          target: { firstName: contactFirstName, email: email || undefined },
        });
        setOffChannel('email');
      } catch (err) {
        console.warn('circle invite: detection failed', err);
        if (cancelled) return;
        setDetection({
          status: 'not-on-pplkpr',
          target: { firstName: contactFirstName, email: email || undefined },
        });
        setOffChannel('email');
      }
    })();
    return () => { cancelled = true; };
  }, [picked, uid]);

  // ── manual lookups ────────────────────────────────────────────────────────
  const lookUpHandle = useCallback(async () => {
    const clean = handleInput.trim().replace(/^@+/, '');
    if (!clean) return;
    setDetection({ status: 'detecting' });
    setLookupError(null);
    try {
      const r = await resolveTetherHandle(clean);
      if (r.found) {
        setDetection({
          status: 'on-pplkpr',
          target: {
            firstName: personalFirstName(r.displayName, r.tetherHandle) || `@${clean}`,
            uid: r.uid,
            tetherHandle: r.tetherHandle ?? null,
          },
        });
      } else {
        setDetection({ status: 'idle' });
        setLookupError(`no one on pplkpr goes by @${clean}.`);
      }
    } catch (err) {
      setDetection({ status: 'idle' });
      setLookupError(err instanceof Error ? err.message : 'could not look up that handle.');
    }
  }, [handleInput]);

  const lookUpEmail = useCallback(async () => {
    const email = emailInput.trim().toLowerCase();
    const typedName = nameInput.trim();
    if (!EMAIL_RE.test(email)) {
      setLookupError('that does not look like an email address.');
      return;
    }
    setDetection({ status: 'detecting' });
    setLookupError(null);
    try {
      const r = await lookupUserByEmail(email);
      if (r.found) {
        setDetection({
          status: 'on-pplkpr',
          target: {
            firstName: personalFirstName(r.displayName, r.tetherHandle) || typedName || 'this person',
            uid: r.uid,
            email,
            tetherHandle: r.tetherHandle ?? null,
          },
        });
      } else {
        setDetection({
          status: 'not-on-pplkpr',
          target: { firstName: typedName || 'they', email },
        });
        setOffChannel('email');
      }
    } catch (err) {
      // A failed lookup must not block the invite — the server re-resolves the
      // address anyway, so fall through to the off-platform email path.
      console.warn('circle invite: email lookup failed', err);
      setDetection({
        status: 'not-on-pplkpr',
        target: { firstName: typedName || 'they', email },
      });
      setOffChannel('email');
    }
  }, [emailInput, nameInput]);

  // ── send ──────────────────────────────────────────────────────────────────
  const target = detection.status === 'on-pplkpr' || detection.status === 'not-on-pplkpr'
    ? detection.target
    : null;

  const effectiveEmail = (target?.email || '').trim()
    || (EMAIL_RE.test(typedEmail.trim()) ? typedEmail.trim() : '');

  const canSend = !!target && !sending && (
    detection.status === 'on-pplkpr' || offChannel === 'link' || !!effectiveEmail
  );

  const send = useCallback(async () => {
    if (!target) return;
    setSending(true);
    setSendError(null);
    setCopied(false);
    let channel: CircleInviteChannel = 'in_app';
    if (detection.status === 'not-on-pplkpr') channel = offChannel === 'link' ? 'link' : 'email';
    try {
      const res = await createCircleInvite({
        circleId,
        ...(target.uid ? { inviteeId: target.uid } : {}),
        // A link invite carries no recipient at all — that's the whole point.
        ...(!target.uid && channel !== 'link' && effectiveEmail ? { inviteeEmail: effectiveEmail } : {}),
        ...(target.firstName && !NAME_PLACEHOLDERS.has(target.firstName.toLowerCase())
          ? { inviteeName: target.firstName }
          : {}),
        channel,
      });
      if (res.success === false) {
        // Spam gate: the callable resolves rather than throwing.
        setSendError(res.reason || 'we could not send that invitation right now.');
        return;
      }
      setResult(res);
      setSentChannel(channel);
      // The server mints a token (and therefore an inviteUrl) for EVERY
      // invitation, including in-app ones — so only the "copy a link" channel
      // may touch the clipboard. Writing on the other channels silently
      // clobbers whatever the admin had copied, for a link they never asked for.
      if (channel === 'link' && res.inviteUrl && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(res.inviteUrl);
          setCopied(true);
        } catch {
          /* clipboard blocked — the URL is still shown for manual copying */
        }
      }
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'could not send the invitation.');
    } finally {
      setSending(false);
    }
  }, [target, detection.status, offChannel, effectiveEmail, circleId]);

  const resetTarget = () => {
    setPicked(null);
    setDetection({ status: 'idle' });
    setLookupError(null);
    setSendError(null);
    setTypedEmail('');
  };

  const startAnother = () => {
    setResult(null);
    setCopied(false);
    setHandleInput('');
    setNameInput('');
    setEmailInput('');
    setQuery('');
    resetTarget();
  };

  // ── render ────────────────────────────────────────────────────────────────

  const firstName = target?.firstName || 'them';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`invite people to ${circleName}`}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(6,2,10,0.72)', backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto',
          borderRadius: 18, padding: 22,
          background: 'linear-gradient(180deg, rgba(30,14,40,0.98), rgba(18,8,26,0.98))',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              invite people
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'rgba(232,220,250,0.6)' }}>
              to {circleName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            style={{
              appearance: 'none', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.05)', color: '#fff',
              borderRadius: 999, width: 30, height: 30,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* ── success ────────────────────────────────────────────────────── */}
        {result ? (
          <div>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 11,
              padding: '14px 16px', borderRadius: 14, marginBottom: 16,
              background: 'rgba(217,70,239,0.10)', border: '1px solid rgba(217,70,239,0.26)',
            }}>
              <Check size={16} style={{ color: '#f0abfc', flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.55 }}>
                {result.created === false
                  ? (result.resendThrottled
                    ? `${firstName} already has a pending invitation — we sent one within the last hour, so we didn't send another.`
                    : result.resent
                      ? `${firstName} already had a pending invitation, so we sent it again.`
                      : `${firstName} already has a pending invitation to this circle.`)
                  : result.isPlaceholder
                    ? `invitation created. ${firstName} isn't on pplkpr yet — the link below is how they join.`
                    : `invitation sent to ${firstName}. they'll see it in pplkpr and by email.`}
              </div>
            </div>

            {/* Every invitation gets a token back, but the link is only part of
                the story for the two channels that have no other way in: the
                explicit "copy a link" choice, and a not-yet-registered invitee.
                Showing it after an in-app invite just invites the admin to
                forward a link that is bound to someone else's uid anyway. */}
            {result.inviteUrl && (sentChannel === 'link' || result.isPlaceholder) && (
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: 'rgba(232,220,250,0.5)', marginBottom: 8,
                }}>
                  invite link
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <code style={{
                    flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', fontSize: 12, color: 'rgba(232,220,250,0.85)',
                  }}>
                    {result.inviteUrl}
                  </code>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(result.inviteUrl as string);
                        setCopied(true);
                      } catch {
                        setCopied(false);
                      }
                    }}
                    style={{
                      appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
                      padding: '7px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                      background: 'rgba(217,70,239,0.16)', border: '1px solid rgba(217,70,239,0.36)',
                      color: '#f0abfc',
                    }}
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'copied' : 'copy'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="primary-button" onClick={startAnother}>
                invite someone else
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  padding: '10px 18px', borderRadius: 12, fontSize: 13.5, fontWeight: 600,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(232,220,250,0.75)',
                }}
              >
                done
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* ── tabs ────────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 7, marginBottom: 16, flexWrap: 'wrap' }}>
              <TabButton
                on={tab === 'people'}
                onClick={() => { setTab('people'); resetTarget(); }}
                icon={<Users size={12} />}
                label="your people"
              />
              <TabButton
                on={tab === 'handle'}
                onClick={() => { setTab('handle'); resetTarget(); }}
                icon={<AtSign size={12} />}
                label="@handle"
              />
              <TabButton
                on={tab === 'email'}
                onClick={() => { setTab('email'); resetTarget(); }}
                icon={<Mail size={12} />}
                label="name + email"
              />
            </div>

            {/* ── people tab ──────────────────────────────────────────────── */}
            {tab === 'people' && !picked && (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '10px 13px', borderRadius: 12, marginBottom: 10,
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                }}>
                  <Search size={14} style={{ color: 'rgba(232,220,250,0.5)', flexShrink: 0 }} />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="search your people…"
                    style={{
                      flex: 1, background: 'transparent', border: 0, outline: 'none',
                      color: '#fff', fontSize: 13.5, fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                  {contactsLoading && (
                    <div style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.5)', padding: '10px 2px' }}>
                      loading your people…
                    </div>
                  )}
                  {!contactsLoading && filteredContacts.length === 0 && (
                    <div style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.5)', padding: '10px 2px', lineHeight: 1.55 }}>
                      no one here matches. use the @handle or name + email tab instead.
                    </div>
                  )}
                  {filteredContacts.map((c) => {
                    const label = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.nickname || 'unnamed';
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setPicked(c)}
                        style={{
                          appearance: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                          display: 'flex', alignItems: 'center', gap: 11,
                          padding: '8px 10px', borderRadius: 12,
                          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                          color: '#fff',
                        }}
                      >
                        <MonoAvatar name={label} size={32} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{label}</span>
                          {c.tetherHandle && (
                            <span style={{ display: 'block', fontSize: 11.5, color: 'rgba(232,220,250,0.5)' }}>
                              @{c.tetherHandle.replace(/^@/, '')}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── handle tab ──────────────────────────────────────────────── */}
            {tab === 'handle' && detection.status === 'idle' && (
              <div>
                <label
                  htmlFor="circle-invite-handle"
                  style={{ display: 'block', fontSize: 12.5, color: 'rgba(232,220,250,0.65)', marginBottom: 8 }}
                >
                  their pplkpr handle
                </label>
                <input
                  id="circle-invite-handle"
                  value={handleInput}
                  onChange={(e) => setHandleInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void lookUpHandle(); }}
                  placeholder="@handle"
                  autoComplete="off"
                  style={inputStyle}
                />
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => void lookUpHandle()}
                  disabled={!handleInput.trim()}
                  style={{ marginTop: 12 }}
                >
                  find them
                </button>
              </div>
            )}

            {/* ── email tab ───────────────────────────────────────────────── */}
            {tab === 'email' && detection.status === 'idle' && (
              <div>
                <label
                  htmlFor="circle-invite-name"
                  style={{ display: 'block', fontSize: 12.5, color: 'rgba(232,220,250,0.65)', marginBottom: 8 }}
                >
                  their first name
                </label>
                <input
                  id="circle-invite-name"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="e.g. sam"
                  autoComplete="off"
                  style={inputStyle}
                />
                <label
                  htmlFor="circle-invite-email"
                  style={{ display: 'block', fontSize: 12.5, color: 'rgba(232,220,250,0.65)', margin: '12px 0 8px' }}
                >
                  their email
                </label>
                <input
                  id="circle-invite-email"
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void lookUpEmail(); }}
                  placeholder="name@example.com"
                  autoComplete="off"
                  style={inputStyle}
                />
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => void lookUpEmail()}
                  disabled={!EMAIL_RE.test(emailInput.trim())}
                  style={{ marginTop: 12 }}
                >
                  continue
                </button>
              </div>
            )}

            {lookupError && (
              <div style={{
                marginTop: 12, padding: '10px 13px', borderRadius: 12,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                fontSize: 12.5, color: '#fca5a5', lineHeight: 1.5,
              }}>
                {lookupError}
              </div>
            )}

            {/* ── detecting ───────────────────────────────────────────────── */}
            {detection.status === 'detecting' && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 12,
                background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
                fontSize: 13, color: 'rgba(232,220,250,0.65)',
              }}>
                <Loader size={14} /> <span>checking if they&apos;re on pplkpr…</span>
              </div>
            )}

            {/* ── resolved target ─────────────────────────────────────────── */}
            {target && (
              <div style={{ marginTop: 4 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 14, marginBottom: 14,
                  background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.09)',
                }}>
                  <MonoAvatar name={firstName} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{firstName}</div>
                    <div style={{ fontSize: 12, color: 'rgba(232,220,250,0.6)' }}>
                      {detection.status === 'on-pplkpr' ? 'on pplkpr' : 'not on pplkpr yet'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetTarget}
                    style={{
                      appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent',
                      color: '#f0abfc', fontWeight: 600, fontSize: 12.5, fontFamily: 'inherit',
                    }}
                  >
                    change
                  </button>
                </div>

                {detection.status === 'on-pplkpr' && (
                  <div style={{
                    padding: '11px 14px', borderRadius: 12, marginBottom: 14,
                    background: 'rgba(217,70,239,0.08)', border: '1px solid rgba(217,70,239,0.22)',
                    fontSize: 12.5, color: '#f3e8ff', lineHeight: 1.55,
                  }}>
                    we&apos;ll send {firstName} a notification and an email. accepting adds them
                    to the circle straight away — your invitation is the approval.
                  </div>
                )}

                {detection.status === 'not-on-pplkpr' && (
                  <>
                    <div style={{
                      padding: '11px 14px', borderRadius: 12, marginBottom: 10,
                      background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.22)',
                      fontSize: 12.5, color: '#fcd9b6', lineHeight: 1.55,
                    }}>
                      {firstName} isn&apos;t on pplkpr yet — choose how to reach them.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                      <ChannelRow
                        on={offChannel === 'email'}
                        onClick={() => setOffChannel('email')}
                        label="email them"
                        sub={effectiveEmail
                          ? `we'll send the invitation to ${effectiveEmail}`
                          : 'type their email address below'}
                        icon={<Mail size={13} style={{ color: 'rgba(232,220,250,0.75)' }} />}
                      />
                      {!target.email && offChannel === 'email' && (
                        <input
                          type="email"
                          value={typedEmail}
                          onChange={(e) => setTypedEmail(e.target.value)}
                          placeholder={`${firstName}'s email address`}
                          autoComplete="off"
                          style={{ ...inputStyle, border: '1px solid rgba(217,70,239,0.28)' }}
                        />
                      )}
                      <ChannelRow
                        on={offChannel === 'link'}
                        onClick={() => setOffChannel('link')}
                        label="copy a link"
                        sub="we'll copy a private link — send it however you like"
                        icon={<Link2 size={13} style={{ color: 'rgba(232,220,250,0.75)' }} />}
                      />
                    </div>
                  </>
                )}

                <button
                  type="button"
                  className="primary-button"
                  onClick={() => void send()}
                  disabled={!canSend}
                >
                  {sending
                    ? 'sending…'
                    : detection.status === 'not-on-pplkpr' && offChannel === 'link'
                      ? 'create the link'
                      : 'send the invitation'}
                </button>

                {sendError && (
                  <div style={{
                    marginTop: 12, padding: '10px 13px', borderRadius: 12,
                    background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                    fontSize: 12.5, color: '#fca5a5', lineHeight: 1.5,
                  }}>
                    {sendError}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
